import React, { useEffect, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { Input } from "src/base-components/Input";
import { Switch } from "src/base-components/Switch";
import { DecisionFlowInputs } from "src/flowsOverview/types";

type ConfigOptionProps = {
  title: string;
  description: string;
  onChange: (value: boolean) => void;
  value: boolean;
};

const ConfigOption: React.FC<ConfigOptionProps> = ({
  title,
  description,
  onChange,
  value,
}) => {
  return (
    <div className="flex gap-x-2">
      <div>
        <h3 className="mb-0.5 text-gray-800 font-inter-semibold-13px">
          {title}
        </h3>
        <p className="text-gray-500 font-inter-normal-12px">{description}</p>
      </div>
      <Switch
        dataLoc="test-run-parallelization-toggle"
        enabled={value}
        onChange={onChange}
      />
    </div>
  );
};

const useScrollIntoViewIf = ({
  condition,
  ref,
}: {
  condition: boolean;
  ref: React.RefObject<HTMLInputElement>;
}) => {
  useEffect(() => {
    if (condition && ref.current) {
      ref.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [condition, ref]);
};

export const TestRunConfigEditor: React.FC = () => {
  const { watch, trigger, control, setValue } =
    useFormContext<DecisionFlowInputs>();

  const workerCount = watch("testRunConfig.worker_count");
  const enableParallelization =
    workerCount !== null && workerCount !== undefined;

  const inputRef = useRef<HTMLInputElement>(null);

  // Combined useEffect for validation and scrolling
  useEffect(() => {
    if (enableParallelization) {
      trigger("testRunConfig.worker_count");
    }
  }, [enableParallelization, trigger]);

  useScrollIntoViewIf({
    condition: enableParallelization,
    ref: inputRef,
  });

  return (
    <div className="flex flex-col gap-y-4">
      <ConfigOption
        description="By default, Taktile adjusts the number of parallel workers based on dataset size and flow characteristics. This option allows you to manually override the default parallelization and set the number of workers."
        title="Override parallelization within test runs"
        value={enableParallelization}
        onChange={(hasValue) =>
          setValue("testRunConfig.worker_count", hasValue ? 5 : null)
        }
      />
      {enableParallelization && (
        <div className="flex flex-col">
          <h3 className="mb-1 text-gray-800 font-inter-semibold-13px">
            Select maximum number of parallel workers
          </h3>
          <p className="mb-2 text-gray-500 font-inter-normal-12px">
            Datasets will be divided to run evenly across the selected number of
            workers. Increasing the number of workers does not always speed up
            test runs due to processing overhead.
          </p>
          <Controller
            control={control}
            name="testRunConfig.worker_count"
            render={({ field }) => (
              <Input
                ref={inputRef}
                data-loc="worker-count-input"
                errored={
                  field.value !== null && (field.value < 1 || field.value > 50)
                }
                max={50}
                min={1}
                placeholder="Number of workers (1-50)"
                type="number"
                value={field.value ?? ""}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (!isNaN(value)) {
                    field.onChange(value);
                  }
                }}
              />
            )}
            rules={{
              required: enableParallelization,
              min: 1,
              max: 50,
            }}
          />
        </div>
      )}
    </div>
  );
};
